<template>
  <RcDialog :model-value="true" title="Axle - ReadyChek AI" :width="mobile ? '100%' : '600px'"
    @closeDialog="handleClose" :fullscreen="mobile">
    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-4 mb-2">
      {{ error }}
    </v-alert>
    
    <div class="chat-messages pa-2" ref="messagesContainer">
      <div v-for="(msg, index) in messages" :key="index" class="d-flex mb-4"
        :class="msg.role === 'user' ? 'justify-end' : 'justify-start'">
          <v-avatar v-if="msg.role === 'ai'" color="primary" size="36" class="mr-3 mt-1">
            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
          </v-avatar>

          <div class="message-bubble pa-3 rounded-lg"
            :class="msg.role === 'user' ? 'bg-primary text-white user-bubble' : 'bg-grey-lighten-4 ai-bubble'"
            style="max-width: 80%;">
            <div v-if="msg.role === 'ai'" class="text-body-1 markdown-body" v-html="marked.parse(msg.content)"></div>
            <div v-else class="text-body-1">{{ msg.content }}</div>
            <div class="text-caption mt-1 text-right"
              :class="msg.role === 'user' ? 'text-white-opacity-70' : 'text-grey'">
              {{ formatTime(msg.timestamp) }}
            </div>
          </div>
        </div>

        <div v-if="isTyping" class="d-flex mb-4 justify-start">
          <v-avatar color="primary" size="36" class="mr-3 mt-1">
            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
          </v-avatar>
          <div class="message-bubble pa-3 rounded-lg bg-grey-lighten-4 ai-bubble d-flex align-center">
            <div class="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>

    <template #actions>
      <div class="w-100 px-2 pb-2 pt-1">
        <v-form @submit.prevent="sendMessage">
          <v-text-field v-model="newMessage" placeholder="Ask about ReadyChek, Hours of Service..."
            variant="outlined" density="comfortable" hide-details class="rounded-lg" bg-color="white">
            <template v-slot:append-inner>
              <v-btn icon="mdi-send" variant="text" color="primary" :disabled="!newMessage.trim() || isTyping"
                @click="sendMessage" />
            </template>
          </v-text-field>
        </v-form>
      </div>
    </template>
  </RcDialog>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useStore } from 'vuex';
import RcDialog from '@/v3/components/library/dialog/RcDialog.vue';
import { marked } from 'marked';
const emit = defineEmits(['closeDialog']);

const { mobile } = useDisplay();
const store = useStore();

const domain = computed(() => store.getters.domain);

const error = ref(null);
const messages = ref([
  {
    role: 'ai',
    content: 'Hello! I am Axle, your ReadyChek AI Assistant. How can I help you today with Hours of Service, Inspections, or anything else ReadyChek related?',
    timestamp: new Date()
  }
]);

const newMessage = ref('');
const isTyping = ref(false);
const messagesContainer = ref(null);

const scrollToBottom = async () => {
  await nextTick();
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const formatTime = (date) => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const handleClose = () => {
  emit('closeDialog');
};

const sendMessage = async () => {
  if (!newMessage.value.trim() || isTyping.value) return;

  const userText = newMessage.value.trim();
  messages.value.push({
    role: 'user',
    content: userText,
    timestamp: new Date()
  });

  newMessage.value = '';
  scrollToBottom();

  // Simulate AI response
  isTyping.value = true;
  scrollToBottom();

  const params = { 'prompt': userText };
  const operation = domain.value.events().ai().prompt(params);
  operation.send((notification, context) => {
    if (notification.error()) {
      messages.value.push({
        role: 'ai',
        content: 'Ouch, that question hurt my brain ' + notification.error().hint,
        timestamp: new Date()
      });
      scrollToBottom();
    } else {
      if (context) {
        //
      }
      const responseText = notification.payload().getString("response");
      messages.value.push({
        role: 'ai',
        content: responseText,
        timestamp: new Date()
      });
      scrollToBottom();
    }
    isTyping.value = false;
  }, {});
};

onMounted(() => {
  scrollToBottom();
});
</script>

<style scoped>
.chat-messages {
  background-color: transparent;
}

.chat-messages {
  background-color: #f8f9fa;
}

.user-bubble {
  border-bottom-right-radius: 4px !important;
}

.ai-bubble {
  border-bottom-left-radius: 4px !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Typing Indicator Animation */
.typing-indicator {
  display: flex;
  align-items: center;
  height: 20px;
}

.typing-indicator span {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #90a4ae;
  border-radius: 50%;
  margin: 0 2px;
  animation: typing 1.4s infinite both;
}

.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {

  0%,
  80%,
  100% {
    transform: scale(0);
    opacity: 0.5;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Markdown Styles */
.markdown-body :deep(p) {
  margin-bottom: 0.5rem;
}
.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}
.markdown-body :deep(a) {
  color: #1976D2;
}
.markdown-body :deep(ul), .markdown-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}
.markdown-body :deep(code) {
  background-color: #eeeeee;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
}
.markdown-body :deep(pre) {
  background-color: #eeeeee;
  padding: 0.8rem;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 0.5rem;
}
</style>
